import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  Loader2,
  RefreshCcw,
  Zap,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Music,
  Sparkles,
  Apple,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';

/**
 * SongstatsPanel — cache-first "Charts & Playlists" section on Track Deep Dive.
 *
 * Behaviour:
 *   * On mount, calls GET /songstats/track/{card_id}. If the backend cache
 *     already has data, we get it back for free (no Songstats call spent).
 *   * If the backend fires a call, the response includes `source: 'api'` +
 *     usage counters so we can render "1 of 25 calls used this month".
 *   * A "Refresh" button appears once cached data is > 7 days old. Users
 *     spend a call knowingly.
 *   * If we're over the monthly budget, the backend returns a "budget
 *     reached" note with stale cached data (still useful) rather than 429.
 *   * When Songstats returns an Apple Music deep-link that the local card
 *     doesn't yet have stored (Phase AM1.1 backfill), we surface a
 *     one-click "Save Apple Music link" button that writes the URL/ID to
 *     the card server-side. This costs zero extra Songstats calls.
 *
 * Kept as a self-contained panel so we can drop it into MusicCardDetail
 * without expanding that already-large file.
 */
export default function SongstatsPanel({ cardId, hasSpotifyMatch = true, seedCard = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(false); // regional charts collapse
  const [appleSaved, setAppleSaved] = useState(false);
  const [applyingApple, setApplyingApple] = useState(false);

  const load = useCallback(async () => {
    if (!cardId || !hasSpotifyMatch) return;
    setLoading(true);
    try {
      const { data: d } = await api.get(`/songstats/track/${cardId}`);
      setData(d);
    } catch (e) {
      setData({ source: 'unavailable', error: e?.response?.data?.detail || 'fetch_failed' });
    } finally {
      setLoading(false);
    }
  }, [cardId, hasSpotifyMatch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data: d } = await api.post(`/songstats/track/${cardId}/refresh`);
      setData(d);
      if (d?.source === 'api') {
        toast.success('Chart data refreshed');
      } else if (d?.error === 'monthly_quota_reached') {
        toast.info('Monthly Songstats budget reached — showing last cached data.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  }, [cardId]);

  // Phase AM1.1 backfill derivations. Hoisted above the early returns
  // so the hook call order stays identical across every render
  // (rules-of-hooks). Both memos handle null `data` gracefully — when
  // the panel is still loading or Songstats has no result, the
  // derivations resolve to `null` / `false` and the component's early
  // returns fire before these values are ever read.
  const cardHasAppleMusic = useMemo(
    () => !!(
      seedCard?.apple_music_track_url ||
      seedCard?.apple_music_track_id ||
      seedCard?.itunes_view_url
    ),
    [
      seedCard?.apple_music_track_url,
      seedCard?.apple_music_track_id,
      seedCard?.itunes_view_url,
    ],
  );
  const songstatsAppleTrackId = data?.apple_music_track_id || null;
  const songstatsAppleTrackUrl = data?.apple_music_track_url || null;
  const canOfferAppleBackfill = useMemo(
    () =>
      !cardHasAppleMusic &&
      !appleSaved &&
      (!!songstatsAppleTrackId || !!songstatsAppleTrackUrl),
    [cardHasAppleMusic, appleSaved, songstatsAppleTrackId, songstatsAppleTrackUrl],
  );

  if (!hasSpotifyMatch) return null;

  // Compact loading state so we don't jank the Deep Dive layout.
  if (loading) {
    return (
      <div
        className="mt-4 rounded-xl border border-border bg-card/40 p-3 flex items-center gap-2 text-xs text-muted-foreground"
        data-testid="songstats-panel-loading"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading chart data…
      </div>
    );
  }

  if (!data || data.source === 'unavailable') {
    // Only render an explanatory note if the card wasn't matched to Spotify.
    if (data?.error === 'no_spotify_id') {
      return (
        <div
          className="mt-4 rounded-xl border border-dashed border-border bg-card/40 p-3 text-xs text-muted-foreground"
          data-testid="songstats-panel-no-spotify"
        >
          <div className="flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
            <span>Match this card to Spotify to unlock chart data + playlist appearances.</span>
          </div>
        </div>
      );
    }
    if (data?.error === 'monthly_quota_reached') {
      return (
        <div
          className="mt-4 rounded-xl border border-dashed border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-500"
          data-testid="songstats-panel-quota-reached"
        >
          <div className="flex items-center gap-1.5">
            <BarChart3 className="w-3.5 h-3.5" />
            <span>Songstats monthly budget reached \u2014 chart data resumes next month.</span>
          </div>
        </div>
      );
    }
    // Data unavailable for another reason (Songstats doesn't know this
    // track, or the API returned an error). Render a subtle notice rather
    // than hiding silently so users don't wonder if the section is broken.
    return (
      <div
        className="mt-4 rounded-xl border border-dashed border-border bg-card/40 p-3 text-xs text-muted-foreground"
        data-testid="songstats-panel-empty"
      >
        <div className="flex items-center gap-1.5">
          <BarChart3 className="w-3.5 h-3.5 text-muted-foreground" />
          <span>Chart data not available for this track yet.</span>
        </div>
      </div>
    );
  }

  const playlistsSpotify = data.playlists_spotify || [];
  const playlistsApple = data.playlists_apple_music || [];
  const totalPlaylists = playlistsSpotify.length + playlistsApple.length;
  const cachedAgeDays = data.cached_at
    ? Math.floor((Date.now() - new Date(data.cached_at).getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const showRefreshButton = cachedAgeDays !== null && cachedAgeDays >= 7;
  const quotaTight = data.quota_limit && data.quota_used >= data.quota_limit - 3;

  const handleSaveAppleLink = async () => {
    if (applyingApple) return;
    setApplyingApple(true);
    try {
      await api.post(`/cards/${cardId}/apple-music/apply`, {
        apple_music_track_id: songstatsAppleTrackId,
        apple_music_track_url: songstatsAppleTrackUrl,
      });
      setAppleSaved(true);
      toast.success('Apple Music link saved to this track');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save Apple Music link');
    } finally {
      setApplyingApple(false);
    }
  };

  return (
    <div
      className="mt-4 rounded-xl border border-border bg-card/40 p-3"
      data-testid="songstats-panel"
    >
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <div className="flex items-center gap-1.5">
          <BarChart3 className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
          <h4 className="text-sm font-semibold">Charts &amp; Playlists</h4>
          {data.is_remix && (
            <Badge
              variant="outline"
              className="ml-1 h-4 px-1.5 text-[9px] uppercase tracking-widest border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))]"
              data-testid="songstats-remix-badge"
            >
              <Zap className="w-2.5 h-2.5 mr-0.5" /> Remix
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {showRefreshButton && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handleRefresh}
              disabled={refreshing}
              className="h-6 px-2 text-[10px]"
              data-testid="songstats-refresh"
            >
              {refreshing ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <RefreshCcw className="w-3 h-3 mr-1" />
              )}
              Refresh
            </Button>
          )}
        </div>
      </div>

      {/* Stream count + provenance */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="rounded-lg bg-background border border-border py-2 px-2 text-center">
          <div className="text-lg font-display font-semibold text-foreground tabular-nums">
            {data.stream_count_spotify != null
              ? formatBigNumber(data.stream_count_spotify)
              : '\u2014'}
          </div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
            Spotify streams
          </div>
        </div>
        <div className="rounded-lg bg-background border border-border py-2 px-2 text-center">
          <div className="text-lg font-display font-semibold text-foreground tabular-nums">
            {totalPlaylists}
          </div>
          <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">
            On playlists
          </div>
        </div>
      </div>

      {(canOfferAppleBackfill || appleSaved) && (
        <div
          className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 py-1.5 px-2"
          data-testid="songstats-apple-backfill"
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <Apple className="w-3.5 h-3.5 text-[hsl(var(--primary))] shrink-0" />
            <span className="text-[11px] text-foreground truncate">
              {appleSaved
                ? 'Apple Music link saved to this track'
                : 'Songstats has an Apple Music link for this track'}
            </span>
          </div>
          {appleSaved ? (
            <Badge
              variant="outline"
              className="h-5 px-1.5 text-[9px] font-mono uppercase border-[hsl(var(--primary))]/50 text-[hsl(var(--primary))] shrink-0"
            >
              <Check className="w-2.5 h-2.5 mr-0.5" /> Saved
            </Badge>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              onClick={handleSaveAppleLink}
              disabled={applyingApple}
              className="h-6 px-2 text-[10px] shrink-0"
              data-testid="songstats-apple-backfill-save"
            >
              {applyingApple ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Apple className="w-3 h-3 mr-1" />
              )}
              Save link
            </Button>
          )}
        </div>
      )}

      {/* Playlist appearances */}
      {totalPlaylists > 0 ? (
        <div className="space-y-2">
          {playlistsSpotify.length > 0 && (
            <PlaylistSection
              title="Spotify playlists"
              playlists={playlistsSpotify.slice(0, expanded ? 30 : 5)}
              testid="songstats-playlists-spotify"
            />
          )}
          {playlistsApple.length > 0 && (
            <PlaylistSection
              title="Apple Music playlists"
              playlists={playlistsApple.slice(0, expanded ? 30 : 5)}
              testid="songstats-playlists-apple"
            />
          )}
          {totalPlaylists > 5 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="w-full text-[11px] text-muted-foreground hover:text-foreground py-1 flex items-center justify-center gap-1"
              data-testid="songstats-expand-toggle"
            >
              {expanded ? (
                <>
                  <ChevronUp className="w-3 h-3" /> Show less
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3" /> Show all {totalPlaylists}
                </>
              )}
            </button>
          )}
        </div>
      ) : (
        <div className="text-[11px] text-muted-foreground py-2">
          Not currently on any tracked editorial playlists.
        </div>
      )}

      {/* Provenance footer */}
      <div className="mt-2 pt-2 border-t border-border/50 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] font-mono text-muted-foreground/80">
          {data.source === 'cache' && cachedAgeDays !== null && (
            <>Cached {cachedAgeDays === 0 ? 'today' : `${cachedAgeDays}d ago`}</>
          )}
          {data.source === 'api' && <>Fresh</>}
          {data.stale && <> · stale (over budget)</>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {quotaTight && (
            <span className="text-[10px] font-mono text-amber-500">
              {data.quota_used}/{data.quota_limit} calls this month
            </span>
          )}
          <a
            href="https://songstats.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[10px] font-mono text-muted-foreground/70 hover:text-[hsl(var(--primary))] transition-colors"
            title="Chart data provided by Songstats"
            data-testid="songstats-attribution"
          >
            Powered by <span className="font-semibold tracking-wide">Songstats</span>
            <ExternalLink className="w-2.5 h-2.5" />
          </a>
        </div>
      </div>
      {data.note && (
        <div className="mt-1.5 text-[10px] text-muted-foreground italic">
          {data.note}
        </div>
      )}
    </div>
  );
}

function PlaylistSection({ title, playlists, testid }) {
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
        {title}
      </div>
      <div className="space-y-1" data-testid={testid}>
        {playlists.map((pl, i) => (
          <div
            key={`${pl.name}-${i}`}
            className="flex items-center gap-2 text-[11px] py-1 px-2 rounded-md bg-background/60 border border-border/50"
          >
            <Music className="w-3 h-3 text-muted-foreground shrink-0" />
            <div className="min-w-0 flex-1 truncate">{pl.name || 'Unknown playlist'}</div>
            {pl.current_position && (
              <div className="text-[10px] font-mono text-[hsl(var(--primary))] tabular-nums shrink-0">
                #{pl.current_position}
              </div>
            )}
            {pl.external_url && (
              <a
                href={pl.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground shrink-0"
                aria-label="Open playlist"
              >
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function formatBigNumber(n) {
  if (typeof n !== 'number') return '—';
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
